import { useAppTranslation } from "@/utils";
import { numberToPersianWords } from "@payping/utils/number";
import { Button, Card, Flex, Form, Input, Typography } from "@payping/web-components";
import { CuToman } from "@payping/icons";
import { FC, useEffect, useState } from "react";
import { PlanData } from "./index";

export interface FormInputs {
  maxCreditAmount: number;
}
type Props = {
  onNext: (value: number) => void;
  planData: PlanData;
};

const PlanAmount: FC<Props> = ({ onNext, planData }) => {
  const { t } = useAppTranslation();
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm<FormInputs>();
  const creditAmountWatch = Form.useWatch("maxCreditAmount ", form) ?? 0;
  const creditAmountWords = () => {
    try {
      const result = numberToPersianWords(creditAmountWatch);
      if (typeof result === "string") {
        return result;
      }
      return null;
    } catch {
      return null;
    }
  };
  useEffect(() => {
    if (planData.maxCreditAmount) {
      form.setFieldValue("maxCreditAmount", planData.maxCreditAmount);
      form.validateFields();
    }
  }, [planData, form]);

  return (
    <Form<FormInputs>
      form={form}
      layout="vertical"
      className="tw-w-full"
      onFieldsChange={() => {
        const hasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
        setIsFormValid(!hasErrors);
      }}
      onFinish={() => {
        const { maxCreditAmount } = form.getFieldsValue();
        onNext(maxCreditAmount);
      }}
    >
      <Flex vertical gap={"large"} className="tw-w-full tw-max-w-[545px] max-md:tw-max-w-[100%]">
        <Flex vertical gap={"middle"}>
          <Typography.Title level={5} className="!tw-m-0">
            {t("shop.bnpl.addInstallmentPlan.steps.setPlanAmount.form.maxCredit.title")}
          </Typography.Title>
          <Card.Ant className="tw-w-full">
            <Form.Item
              name={"maxCreditAmount"}
              label={t("shop.bnpl.addInstallmentPlan.steps.setPlanAmount.form.maxCredit.label")}
              rules={[
                { required: true },
                {
                  validator(_, value) {
                    if (value < 1000) {
                      return Promise.reject(
                        new Error(
                          t("common.validations.min", {
                            name: t("shop.bnpl.addInstallmentPlan.steps.settings.form.maxCredit.label"),
                            min: "1,000",
                          }),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={
                creditAmountWatch <= 0 || !creditAmountWords() ? null : (
                  <Flex align="center">
                    <div className="tw-mb-2 tw-mt-1">{`${creditAmountWords()}`}</div>
                    <CuToman width={24} height={24} />
                  </Flex>
                )
              }
            >
              <Input.Number
                className="tw-w-full tw-text-center"
                style={{ textAlignLast: "center" }}
                inputMode="numeric"
                changeOnWheel={false}
                controls={false}
                separateThousandth
                step={1000}
                addonAfter={<CuToman width={24} height={24} />}
              />
            </Form.Item>
          </Card.Ant>
        </Flex>
        <div>
          <Button
            block={false}
            disabled={!isFormValid}
            onClick={form.submit}
            className="!tw-px-8"
            loading={false}
            type="primary"
          >
            {t("common.saveAndContinue")}
          </Button>
        </div>
      </Flex>
    </Form>
  );
};

export default PlanAmount;
